// Lot 671 — enregistreur de vol : chaque seconde, l'état de la page (chemin, vidéos, images, nœuds, mémoire) est
// noté sur l'appareil. Au démarrage suivant, si la page vient d'être rechargée SANS que l'app l'ait demandé
// (processus WebKit tué par iOS → Capacitor recharge ; WebView Android relancée), un rapport « boot_loop » part
// vers POST /client-crashes avec les derniers relevés → Admin › Supervision › Incidents. Un avertissement mémoire
// iOS (plugin YalolaNative) envoie un rapport « memory » tout de suite. Désactivable : garde-fou « flight_recorder ».
import { noteNativeMemory, noteMemoryWarning, noteRestartLoop } from "@/lib/liteProfile";
import { Capacitor } from "@capacitor/core";
import { reportClientCrash, intentionalReloadPending } from "@/lib/crashReport";
import { guardOn, enterSafeMode } from "@/lib/guards";
import { nativeMemoryInfo, onNativeEvent, nativeLastExits } from "@/lib/yalolaNative";

const KEY = "yalola.flight";
const QUICK_MS = 90 * 1000;   // relancée moins de 90 s après le dernier relevé = redémarrage, pas une réouverture
const CRUMBS_MAX = 30;
const BEAT_MS = 1000;
const MEM_POLL_MS = 15000; // lot 688

const read = () => { try { return JSON.parse(localStorage.getItem(KEY) || "null"); } catch { return null; } };
const write = (st) => { try { localStorage.setItem(KEY, JSON.stringify(st)); } catch { /* quota */ } };
const navType = () => { try { return performance.getEntriesByType("navigation")[0]?.type || ""; } catch { return ""; } };

// Pur (testé) : la page précédente s'est-elle arrêtée sans qu'on l'ait voulu ?
// prev = dernier état enregistré ; intentional = rechargement demandé par l'app ; native = app Android/iOS.
export const classifyBoot = ({ prev, now, type, intentional, native }) => {
  if (!prev || !prev.lastBeat) return { unexpected: false, restarts: 0 };
  const quick = now - prev.lastBeat < QUICK_MS;
  if (!quick || intentional || prev.hidden) return { unexpected: false, restarts: 0 };
  // Web : seul un vrai « reload » compte (F5 du membre compris, d'où le seuil de 2 avant rapport) ; natif : toute relance rapide.
  if (!native && type !== "reload") return { unexpected: false, restarts: 0 };
  return { unexpected: true, restarts: (prev.restarts || 0) + 1 };
};

const fmtCrumb = (c) => `${c.t}s ${c.p} v${c.v}/${c.vp} i${c.i} n${c.n}${c.m != null ? ` js${c.m}Mo` : ""}${c.f != null ? ` app${c.f}Mo/${c.a}Mo` : ""}${c.mw ? " ⚠mémoire" : ""}${c.h ? " (fond)" : ""}`;

let state = null;
let native = false;
let mem = null;      // dernier relevé natif iOS { footprintMb, availableMb }
let installed = false;
let bootUnexpected = false; // lot 682 — la relance en cours a-t-elle été classée involontaire (reprise du fil) ?
export const lastBootUnexpected = () => bootUnexpected;

const snapshot = (extra = {}) => {
  const videos = Array.from(document.querySelectorAll("video"));
  const c = {
    t: Math.round((Date.now() - state.bootAt) / 1000),
    p: `${window.location.pathname}${window.location.search}`.slice(0, 80),
    v: videos.length,
    vp: videos.filter((v) => !v.paused && !v.ended).length,
    i: document.images.length,
    n: document.getElementsByTagName("*").length,
    h: document.visibilityState === "hidden" ? 1 : undefined,
    ...extra,
  };
  try { if (performance.memory) c.m = Math.round(performance.memory.usedJSHeapSize / 1048576); } catch { /* Safari */ }
  if (mem) { c.f = Math.round(mem.footprintMb); c.a = Math.round(mem.availableMb); }
  return c;
};

const beat = (extra) => {
  if (!state) return;
  const c = snapshot(extra);
  state.crumbs = [...state.crumbs, c].slice(-CRUMBS_MAX);
  state.lastBeat = Date.now();
  state.hidden = document.visibilityState === "hidden";
  write(state);
  return c;
};

const platformLabel = () => (native ? Capacitor.getPlatform() : "web");

// Lot 677 — Android : MainActivity note la mort du moteur WebView (mémoire : crash=false ; plantage : crash=true)
// et la rend lisible une fois via window.YalolaInsets.getLastRenderGone() → cause explicite dans le rapport.
export const readAndroidRenderGone = () => {
  try {
    const raw = window.YalolaInsets?.getLastRenderGone?.();
    const d = raw ? JSON.parse(raw) : null;
    if (!d || typeof d !== "object") return "";
    const bg = d.bg ? " pendant que l'app était en arrière-plan, rechargé au retour" : "";
    return d.crash ? ` — moteur WebView Android PLANTÉ${bg || " (puis rechargé par l'app au lieu de la fermer)"}`
      : ` — moteur WebView Android TUÉ PAR LE SYSTÈME (mémoire insuffisante${bg ? ")" + bg : " ; rechargé par l'app au lieu de la fermer)"}`;
  } catch { return ""; }
};

const reportBootLoop = (prev, restarts, type, safeMode) => {
  const crumbs = prev.crumbs || [];
  const last = crumbs[crumbs.length - 1];
  const since = last ? `${last.t} s après son démarrage` : "durée inconnue";
  const where = last ? `${last.p} — ${last.v} vidéo(s) dont ${last.vp} en lecture, ${last.i} images, ${last.n} nœuds${last.m != null ? `, JS ${last.m} Mo` : ""}${last.f != null ? `, app ${last.f} Mo (dispo ${last.a} Mo)` : ""}` : "aucun relevé";
  const message = `Redémarrage inattendu de la page ×${restarts} (${platformLabel()}, navigation « ${type || "?"} ») : la page précédente s'est arrêtée ${since} sur ${where}${readAndroidRenderGone()}${safeMode ? " — mode allégé activé 6 h sur cet appareil (silencieux pour le membre)" : ""}`;
  reportClientCrash({ error: { message, stack: crumbs.map(fmtCrumb).join("\n") }, kind: "boot_loop" });
};

// Lot 672 — mode sécurisé automatique : natif dès 2 redémarrages inattendus d'affilée, web à partir de 3 (deux F5 rapides restent légitimes).
export const safeModeThreshold = (native) => (native ? 2 : 3);

// Lot 681 — « pourquoi l'app s'est fermée » raconté par le système lui-même :
//   • Android 11+ : ApplicationExitInfo (ExitReasons.java, via window.YalolaInsets.getLastExits()) — ANR avec la pile du
//     fil principal que Google Play n'affiche pas, plantage Java/natif, mémoire ;
//   • iOS 14+ : MetricKit (YalolaNativePlugin.lastExits) — « hang » (interface figée) et plantage.
// Chaque sortie n'est renvoyée qu'une fois par le natif ; on n'envoie que la plus récente (le backend ignore un 2e
// rapport du même appareil sous 20 s) → Admin › Supervision › Historique des incidents, kind anr / hang / native_crash / low_memory.
const EXIT_LABEL = {
  anr: "ANR Android — l'app ne répondait plus, l'utilisateur en a été éjecté",
  hang: "Interface iOS figée (hang)",
  native_crash: "Plantage natif",
  low_memory: "App fermée par le système (mémoire insuffisante)",
  excessive_cpu: "App fermée par le système (CPU en arrière-plan)", // lot 699 — REASON_EXCESSIVE_RESOURCE_USAGE (2.4.2+)
};
export const exitMessage = (e, platform) => {
  const when = e.at ? new Date(e.at).toISOString().replace("T", " ").slice(0, 19) : "?";
  const where = e.importance != null ? (e.importance <= 100 ? "au premier plan" : "en arrière-plan") : "";
  const extra = [
    where,
    e.durationS != null ? `${e.durationS} s figée` : "",
    e.pssMb ? `${e.pssMb} Mo` : "",
    e.description || e.reason || "",
  ].filter(Boolean).join(" · ");
  return `${EXIT_LABEL[e.kind] || "Fermeture inattendue"} (${platform}, ${when} UTC${extra ? ` — ${extra}` : ""})`;
};
// Lot 690 — fermeture « mémoire » survenue en arrière-plan (importance ≥ 300 : service / en cache) : Android récupère
// normalement la mémoire des apps inactives, le membre n'a rien vu → pas un plantage, on ne la remonte plus.
// (iOS : les items MetricKit n'ont pas d'importance → jamais filtrés.)
export const isBenignBackgroundExit = (e) => e?.kind === "low_memory" && e?.importance != null && e.importance >= 300;
export const pickLatestExit = (items) => {
  const ok = (items || []).filter((e) => e && EXIT_LABEL[e.kind] && !isBenignBackgroundExit(e));
  ok.sort((a, b) => (b.at || 0) - (a.at || 0));
  return ok[0] || null;
};
// Lot 691 — l'app était-elle au premier plan ? Android 11+ : importance ≤ 100. Sans importance (plantage connu de
// CrashStore seul, iOS MetricKit) : un plantage est supposé vu par le membre → le serveur alerte les super admins
// dès le 1er appareil pour un `native_crash` au premier plan (lien direct vers la pile).
export const exitForeground = (e) => (e?.importance != null ? e.importance <= 100 : true);
const readAndroidExits = () => {
  try { const raw = window.YalolaInsets?.getLastExits?.(); const arr = raw ? JSON.parse(raw) : []; return Array.isArray(arr) ? arr : []; } catch { return []; }
};
export const reportNativeExits = async () => {
  const platform = Capacitor.getPlatform();
  const items = platform === "android" ? readAndroidExits() : platform === "ios" ? await nativeLastExits() : [];
  const e = pickLatestExit(items);
  if (!e) return null;
  // Pile de TOUS les fils condensée (ANR Android, lot 716) ou arbre d'appels (iOS), puis les derniers relevés de l'enregistreur de vol.
  const stack = [e.trace || "", ...(read()?.crumbs || []).map(fmtCrumb)].filter(Boolean).join("\n").slice(0, 16000);
  reportClientCrash({ error: { message: exitMessage(e, platform), stack }, kind: e.kind, foreground: exitForeground(e) });
  return e;
};

export const installFlightRecorder = () => {
  if (installed || typeof window === "undefined" || typeof document === "undefined") return;
  installed = true;
  if (!guardOn("flight_recorder")) return;
  native = Capacitor.isNativePlatform();
  const prev = read();
  const now = Date.now();
  const type = navType();
  const { unexpected, restarts } = classifyBoot({ prev, now, type, intentional: intentionalReloadPending(), native });
  bootUnexpected = unexpected;
  state = { bootAt: now, lastBeat: now, hidden: document.visibilityState === "hidden", restarts, crumbs: [] };
  write(state);
  // Natif : dès la 1re relance inattendue (une app ne se recharge jamais seule) ; web : à partir de 2 (F5 légitime).
  const safeMode = unexpected && restarts >= safeModeThreshold(native);
  if (safeMode) { enterSafeMode(restarts); noteRestartLoop(); }  // lot 734 — appareil qui peine → vidéos allégées 7 j
  if (unexpected && (native || restarts >= 2)) setTimeout(() => reportBootLoop(prev, restarts, type, safeMode), 1500);
  // Lot 681 — raison système de la dernière fermeture (ANR / hang / plantage / mémoire), un peu après le démarrage.
  if (native) setTimeout(() => { reportNativeExits().catch(() => {}); }, 4000);

  setInterval(() => beat(), BEAT_MS);
  document.addEventListener("visibilitychange", () => beat());
  if (native && Capacitor.getPlatform() === "ios") {
    // Lot 688 — relevé mémoire toutes les 15 s (2 s saturait la console Xcode et le pont natif pour rien), seulement
    // au premier plan ; la valeur reste à jour pour le rapport de redémarrage.
    const poll = () => { if (document.visibilityState === "hidden") return; nativeMemoryInfo().then((m) => { if (m && typeof m.footprintMb === "number") { mem = m; noteNativeMemory(m); } }).catch(() => {}); };
    poll();
    setInterval(poll, MEM_POLL_MS);
    onNativeEvent("memoryWarning", (d) => {
      noteMemoryWarning();  // lot 734
      if (d && typeof d.footprintMb === "number") mem = d;
      const c = beat({ mw: 1 });
      const message = `Avertissement mémoire iOS : app ${Math.round(d?.footprintMb ?? -1)} Mo, disponible ${Math.round(d?.availableMb ?? -1)} Mo — ${c ? fmtCrumb(c) : ""}`;
      reportClientCrash({ error: { message, stack: (state?.crumbs || []).map(fmtCrumb).join("\n") }, kind: "memory" });
    });
    // Lot 681 — MetricKit peut livrer un diagnostic pendant que l'app tourne : on l'envoie sans attendre le prochain lancement.
    onNativeEvent("diagnostics", () => { reportNativeExits().catch(() => {}); });
  }
};

export const flightCrumbs = () => (state ? [...state.crumbs] : []);
export const _resetFlightForTests = () => { state = null; installed = false; mem = null; bootUnexpected = false; };
